import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const chartData = [40, 55, 45, 65, 50, 70, 60, 75, 85, 70, 80, 90];

interface HeroSectionProps {
  onDemoClick?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onDemoClick }) => {
  const { t } = useTranslation();

  const months = t('hero.months', { returnObjects: true }) as string[];
  const sidebarItems = t('hero.sidebarItems', { returnObjects: true }) as string[];
  const statLabels = t('hero.statLabels', { returnObjects: true }) as string[];
  const trustTags = t('hero.trustTags', { returnObjects: true }) as string[];

  return (
    <section id="hero" className="hp-hero" aria-labelledby="hero-title">
      <div className="hp-hero-glow hp-hero-glow--1" />
      <div className="hp-hero-glow hp-hero-glow--2" />

      <div className="hp-hero-inner">
        {/* Left: Content */}
        <div className="hp-hero-content">
          <div className="hp-hero-badge hp-fade-in" style={{ animationDelay: '0s' }}>
            <span className="hp-pulse-dot" />
            {t('hero.badge')}
          </div>

          <h1 id="hero-title" className="hp-hero-headline hp-fade-in" style={{ animationDelay: '0.1s' }}>
            {t('hero.headlineLine1')}<br />
            <span className="hp-gradient-text">{t('hero.headlineHighlight')}</span>
          </h1>

          <p className="hp-hero-subtitle hp-fade-in" style={{ animationDelay: '0.2s' }}>
            {t('hero.subtitle')}
            <br />{t('hero.subtitleDsgvo')}
          </p>

          <div className="hp-hero-ctas hp-fade-in" style={{ animationDelay: '0.3s' }}>
            <Link to="/register" className="hp-btn-hero-primary">
              {t('hero.ctaPrimary')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
            <button className="hp-btn-hero-secondary" onClick={() => {
              if (onDemoClick) {
                onDemoClick();
              } else {
                document.querySelector('#platform')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5,3 19,12 5,21" />
              </svg>
              {t('hero.ctaSecondary')}
            </button>
          </div>

          <div className="hp-hero-trust hp-fade-in" style={{ animationDelay: '0.4s' }}>
            <span className="hp-hero-trust-label">{t('hero.trustedBy')}</span>
            {trustTags.map((tag) => (
              <span key={tag} className="hp-hero-trust-tag">{tag}</span>
            ))}
          </div>
        </div>

        {/* Right: App Preview */}
        <div className="hp-hero-visual hp-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="hp-app-preview">
            {/* Browser Bar */}
            <div className="hp-app-bar">
              <span className="hp-dot hp-dot--red" />
              <span className="hp-dot hp-dot--yellow" />
              <span className="hp-dot hp-dot--green" />
            </div>

            {/* App Content */}
            <div className="hp-app-body">
              {/* Sidebar */}
              <div className="hp-app-sidebar">
                {sidebarItems.map((label, i) => (
                  <div
                    key={label}
                    className={`hp-app-sidebar-item${i === 0 ? ' hp-app-sidebar-item--active' : ''}`}
                  >
                    {label}
                  </div>
                ))}
              </div>

              {/* Main */}
              <div className="hp-app-main">
                {/* Stats */}
                <div className="hp-app-stats">
                  <div className="hp-app-stat">
                    <span className="hp-app-stat-value">47</span>
                    <span className="hp-app-stat-label">{statLabels[0]}</span>
                  </div>
                  <div className="hp-app-stat">
                    <span className="hp-app-stat-value">156</span>
                    <span className="hp-app-stat-label">{statLabels[1]}</span>
                  </div>
                  <div className="hp-app-stat">
                    <span className="hp-app-stat-value">&euro;24k</span>
                    <span className="hp-app-stat-label">{statLabels[2]}</span>
                  </div>
                </div>

                {/* Chart */}
                <div className="hp-app-chart">
                  <div className="hp-app-chart-title">{t('hero.chartTitle')}</div>
                  <div className="hp-app-chart-bars">
                    {chartData.map((value, i) => (
                      <div key={months[i]} className="hp-app-chart-col">
                        <div
                          className="hp-app-chart-bar"
                          style={{
                            height: `${value}%`,
                            animationDelay: `${0.5 + i * 0.05}s`,
                          }}
                        />
                        <span className="hp-app-chart-label">{months[i]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
